import React, { useState } from 'react';
import { 
  BookOpen, Search, Calendar, LayoutDashboard, Megaphone, Gift, 
  Package, Award, Bot, DollarSign, ChevronRight, CheckCircle2, 
  HelpCircle, Lightbulb, Sparkles, X, ExternalLink, ShieldCheck, 
  MessageSquare, Users
} from 'lucide-react';

export default function ClientHelpCenter({ onClose, activeBusiness }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [expandedTopic, setExpandedTopic] = useState('agenda');

  const categories = [
    { id: 'todos', label: 'Todos los Temas', icon: BookOpen },
    { id: 'agenda_crm', label: 'Citas & CRM', icon: Calendar },
    { id: 'marketing', label: 'WhatsApp & Campañas', icon: Megaphone },
    { id: 'loyalty', label: 'Cupones & VIP', icon: Gift },
    { id: 'operaciones', label: 'Inventario & Nómina', icon: Package },
    { id: 'ia_bot', label: 'Asistente IA Keyla', icon: Bot },
  ];

  const topics = [
    {
      id: 'agenda',
      category: 'agenda_crm',
      title: '📅 Agenda Inteligente & Sincronización Google Calendar',
      subtitle: 'Cómo gestionar citas, evitar empalmes y sincronizar reservas 24/7.',
      badge: 'Básico',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      steps: [
        {
          title: 'Crear una cita manualmente',
          text: 'Haz clic en cualquier hora libre del calendario en la pestaña "Agenda Inteligente". Completa el nombre del cliente, teléfono (con código de país) y selecciona el servicio.',
          tip: 'Keyla detecta automáticamente la duración del servicio y bloquea el espacio.'
        },
        {
          title: 'Conectar con Google Calendar',
          text: 'En tu configuración general, ingresa tu ID de Google Calendar (o correo asociado). Todas las citas creadas en Keyla aparecerán en Google y viceversa.',
          tip: 'Si bloqueas un espacio en tu Google Calendar personal, el bot no agendará citas en ese horario.'
        },
        {
          title: 'Confirmaciones y Recordatorios automáticos',
          text: 'Keyla envía recordatorios automáticos por WhatsApp antes de la cita para reducir inasistencias en un 85%.'
        }
      ]
    },
    {
      id: 'kanban',
      category: 'agenda_crm',
      title: '📊 CRM Kanban: Embudo Comercial Visita a Visita',
      subtitle: 'Control visual del estado de cada paciente o cliente.',
      badge: 'Ventas',
      badgeColor: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      steps: [
        {
          title: 'Etapas del Embudo',
          text: 'Tu embudo está dividido en columnas estratégicas: Nuevo Lead -> Por Confirmar -> Confirmada -> En Proceso -> Completada -> Cancelada.',
          tip: 'Puedes mover tarjetas entre columnas arrastrándolas (Drag & Drop) o con el selector rápido.'
        },
        {
          title: 'Historial 360° del Cliente',
          text: 'Haz clic en la tarjeta del cliente para consultar sus citas anteriores, historial de pagos, notas internas y preferencias especiales.'
        }
      ]
    },
    {
      id: 'campanas',
      category: 'marketing',
      title: '📢 Campañas de WhatsApp: Difusión Persuasiva',
      subtitle: 'Reactiva clientes dormidos y promociona ofertas en segundos.',
      badge: 'Marketing 💎',
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      steps: [
        {
          title: 'Filtros de Audiencia Inteligentes',
          text: 'No envíes mensajes a ciegas. Utiliza los filtros en vivo: "Clientes VIP" (frecuentes), "Inactivos (+30 días)" o "Recientes" (para pedir reseñas en Google Maps).',
          tip: 'El filtro te muestra en tiempo real cuántas personas recibirán el mensaje.'
        },
        {
          title: 'Plantillas con Variables Dinámicas',
          text: 'Elige entre nuestras plantillas redactadas para alta conversión. Las variables {cliente} y {negocio} se reemplazan automáticamente por el nombre real de cada persona.',
          tip: 'Modo Seguro Anti-Spam: Si envías desde WhatsApp Web, espera de 5 a 10 segundos entre clics para proteger tu número.'
        }
      ]
    },
    {
      id: 'fidelizacion',
      category: 'loyalty',
      title: '🎁 Fidelización VIP & Tarjetas de Puntos (Punch Card)',
      subtitle: 'Convierte clientes casuales en fans recurrentes de tu marca.',
      badge: 'Retención 💎',
      badgeColor: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      steps: [
        {
          title: 'Crear Códigos Promocionales y Cupones',
          text: 'Genera códigos por Porcentaje (%) o Monto Fijo ($). Configura un mínimo de compra, límite de usos y fecha de expiración.',
          tip: 'Usa el botón "Compartir por WhatsApp" para enviar al cliente un enlace mágico que aplica el descuento automáticamente.'
        },
        {
          title: 'Programa de Lealtad (Sellos Digitales)',
          text: 'Define cuántas visitas necesita un cliente para ganar un premio (ej. 5 visitas = 1 Servicio Gratis o 50% de descuento).',
          tip: 'El cliente puede consultar sus puntos acumulados en tiempo real desde tu enlace de reservas.'
        }
      ]
    },
    {
      id: 'inventario',
      category: 'operaciones',
      title: '📦 Inventario & Alertas de Stock Inteligentes',
      subtitle: 'Evita quiebres de inventario y controla costos de insumos.',
      badge: 'Operaciones',
      badgeColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      steps: [
        {
          title: 'Registro de Productos y Stock Mínimo',
          text: 'Ingresa cada producto con su costo, precio de venta, cantidad disponible y stock crítico de alerta.',
          tip: 'Las tarjetas en rojo te avisan qué insumos están por agotarse para que reordenes a tiempo.'
        },
        {
          title: 'Movimientos Rápidos (+ / -)',
          text: 'Registra entradas por compra a proveedor o salidas por consumo interno con un solo clic.'
        }
      ]
    },
    {
      id: 'comisiones',
      category: 'operaciones',
      title: '🤝 Comisiones & Nómina Automática de Especialistas',
      subtitle: 'Cálculo transparente del pago a cada miembro de tu equipo.',
      badge: 'Finanzas',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      steps: [
        {
          title: 'Cálculo en Vivo por Citas Pagadas',
          text: 'El sistema suma las citas completadas y pagadas de cada especialista y aplica su porcentaje de comisión asignado.',
          tip: 'Puedes cambiar el % de comisión de un empleado y el cálculo se actualiza al instante.'
        },
        {
          title: 'Desglose por WhatsApp & Exportación CSV',
          text: 'Haz clic en el botón de WhatsApp para enviarle a tu empleado su resumen oficial de nómina, o descarga el reporte en Excel/CSV.'
        }
      ]
    },
    {
      id: 'bot_ia',
      category: 'ia_bot',
      title: '🤖 Asistente IA Keyla: Tu Recepcionista 24/7',
      subtitle: 'Cómo entrenar la IA con la identidad de tu negocio.',
      badge: 'IA Core',
      badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      steps: [
        {
          title: 'Personalizar Tono y Estilo',
          text: 'En la pestaña "Identidad IA", describe tu negocio, políticas de cancelación, métodos de pago y si prefieres un tono formal, cercano o entusiasta.',
          tip: 'Keyla responde preguntas frecuentes, cotiza servicios y agenda en segundos sin intervención humana.'
        },
        {
          title: 'Intervención Manual en Chats',
          text: 'Si deseas atender personalmente a un cliente VIP, puedes pausar el bot para ese número o silenciarlo por 24 horas.'
        }
      ]
    }
  ];

  const filteredTopics = topics.filter(t => {
    const matchesCategory = selectedCategory === 'todos' || t.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.steps.some(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()) || s.text.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-fadeIn">
      <div className="bg-seryi-bg border border-seryi-bg-light w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">
        
        {/* Cabecera / Header */}
        <div className="p-6 bg-gradient-to-r from-slate-900 via-indigo-950/60 to-slate-900 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-seryi-green/20 border border-seryi-green/40 flex items-center justify-center text-seryi-green shadow-lg shadow-seryi-green/10">
              <BookOpen size={28} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-seryi-green/20 text-seryi-green border border-seryi-green/30">
                  Keyla Academy
                </span>
                <span className="text-xs text-seryi-text font-mono">v12.4</span>
              </div>
              <h2 className="text-2xl font-black text-seryi-heading tracking-tight mt-1">
                Centro de Ayuda & Manual de Usuario
              </h2>
              <p className="text-xs text-seryi-text">
                Domina todas las herramientas de <strong className="text-seryi-heading">{activeBusiness?.nombre_negocio || 'tu negocio'}</strong> en minutos.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-seryi-text" />
              <input
                type="text"
                placeholder="Buscar tutorial..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-900/80 border border-slate-700 rounded-xl pl-9 pr-4 py-2 text-xs text-seryi-heading placeholder-slate-500 focus:outline-none focus:border-seryi-green"
              />
            </div>
            <button 
              onClick={onClose}
              className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-rose-500/20 hover:text-rose-400 text-seryi-text transition-all"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Barra de Categorías */}
        <div className="px-6 py-3 bg-slate-900/60 border-b border-slate-800/80 overflow-x-auto flex items-center gap-2 no-scrollbar">
          {categories.map(cat => {
            const IconComponent = cat.icon;
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  isSelected 
                    ? 'bg-seryi-green text-seryi-bg-dark shadow-md shadow-seryi-green/20 scale-105'
                    : 'bg-slate-800/60 text-seryi-text hover:text-seryi-heading hover:bg-slate-800'
                }`}
              >
                <IconComponent size={14} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Contenido Principal / Acordeón de Guías */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {filteredTopics.length === 0 ? (
            <div className="text-center py-16 space-y-3">
              <HelpCircle size={48} className="mx-auto text-slate-600" />
              <h3 className="text-base font-bold text-seryi-heading">No encontramos guías para "{searchQuery}"</h3>
              <p className="text-xs text-seryi-text max-w-md mx-auto">
                Intenta con palabras como "citas", "cupones", "whatsapp", "inventario" o selecciona la categoría "Todos los Temas".
              </p>
            </div>
          ) : (
            filteredTopics.map((topic) => {
              const isExpanded = expandedTopic === topic.id;
              return (
                <div 
                  key={topic.id}
                  className={`border rounded-2xl transition-all duration-200 overflow-hidden ${
                    isExpanded 
                      ? 'bg-slate-900/80 border-seryi-green/40 shadow-lg shadow-seryi-green/5' 
                      : 'bg-seryi-bg/80 border-slate-800/80 hover:border-slate-700'
                  }`}
                >
                  {/* Cabecera del Acordeón */}
                  <button
                    onClick={() => setExpandedTopic(isExpanded ? null : topic.id)}
                    className="w-full p-5 flex items-center justify-between text-left gap-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded border ${topic.badgeColor}`}>
                          {topic.badge}
                        </span>
                        <h3 className="font-extrabold text-sm sm:text-base text-seryi-heading">
                          {topic.title}
                        </h3>
                      </div>
                      <p className="text-xs text-seryi-text">
                        {topic.subtitle}
                      </p>
                    </div>

                    <div className={`p-2 rounded-xl transition-transform duration-200 ${
                      isExpanded ? 'bg-seryi-green/20 text-seryi-green rotate-90' : 'bg-slate-800 text-slate-400'
                    }`}>
                      <ChevronRight size={18} />
                    </div>
                  </button>

                  {/* Cuerpo Desplegable */}
                  {isExpanded && (
                    <div className="px-5 pb-6 pt-2 border-t border-slate-800/80 space-y-4 bg-slate-950/40">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {topic.steps.map((step, idx) => (
                          <div 
                            key={idx}
                            className="bg-seryi-bg/90 border border-slate-800/80 rounded-xl p-4 flex flex-col justify-between space-y-3 relative overflow-hidden group hover:border-seryi-green/30 transition-all"
                          >
                            <div className="absolute top-0 right-0 w-16 h-16 bg-seryi-green/5 rounded-bl-full pointer-events-none" />
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-seryi-green/20 text-seryi-green font-black text-xs flex items-center justify-center border border-seryi-green/30">
                                  {idx + 1}
                                </span>
                                <h4 className="font-bold text-xs text-seryi-heading">
                                  {step.title}
                                </h4>
                              </div>
                              <p className="text-xs text-slate-300 leading-relaxed">
                                {step.text}
                              </p>
                            </div>

                            {step.tip && (
                              <div className="pt-2 border-t border-slate-800 flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/5 p-2.5 rounded-lg border border-amber-500/20">
                                <Lightbulb size={14} className="shrink-0 mt-0.5 text-amber-400" />
                                <span>{step.tip}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pie / Footer con Soporte Rápido */}
        <div className="p-4 bg-slate-900/90 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck size={16} className="text-seryi-green" />
            <span>¿Tienes dudas técnicas o quieres una capacitación personalizada para tu staff?</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://wa.me/573170000000?text=Hola%20Soporte%20Keyla%2C%20necesito%20ayuda%20con%20mi%20pesta%C3%B1a%20en%20Seryiads"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-seryi-green/20 hover:bg-seryi-green/30 text-seryi-green border border-seryi-green/40 text-xs font-bold transition-all"
            >
              <MessageSquare size={14} /> Contactar Soporte WhatsApp
            </a>
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-xl bg-seryi-green text-seryi-bg-dark font-black text-xs hover:bg-seryi-green/80 transition-all"
            >
              Entendido
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
